import styles from './Cart.scss';

import { captureException } from '@sentry/browser';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory, useLocation } from 'react-router-dom';

import { EmptyCartPage } from 'src/components/Cart/EmptyCartPage/EmptyCartPage';
import { CartProductsList } from 'src/components/CartProductsList/CartProductsList';
import { Grid } from 'src/components/Grid/Grid';
import { GridContainer } from 'src/components/GridContainer/GridContainer';
import { Image } from 'src/components/Image/Image';
import { OrderSummary } from 'src/components/OrderSummary/OrderSummary';
import { TextV2 } from 'src/components/TextV2/TextV2';
import { useDesktop, useDesktopLarge, useMobile } from 'src/hooks/UseResponsive';

import { LinkVariant } from 'src/api/contentful/contentTypes.cloverdotcom';
import { redesignV2RoutePath } from 'src/constants/redesignV2';
import loadingGif from 'src/images/loading.gif';
import { CheckoutRoutePath } from '../Checkout/Checkout';
import { PROGRESS_BAR_STEPS, PROGRESS_BAR_STEP_ENUM } from '../Checkout/CheckoutProgressBar.config';
import { CheckoutProvider } from '../Checkout/CheckoutProvider';
import { PageHeaderBanner } from '../PageHeaderBanner/PageHeaderBanner';
import { ResponsiveProgressBar } from '../ResponsiveProgressBar/ResponsiveProgressBar';
import { StructuredModal } from '../StructuredModal/StructuredModal';
import { MoreAccessories } from './MoreAccessories/MoreAccessories';
import { useCart } from './UseCart';
import { CartURLActionType, getCartURLAction, getCartURLWithoutActions, isCartItemQuantityMax } from './Util';

export const Cart: React.FC = () => {
  const history = useHistory();
  const isMobile = useMobile();
  const isDesktop = useDesktop();
  const isDesktopLarge = useDesktopLarge();
  const { addBundleById, cart, isLoading } = useCart();
  const { t } = useTranslation();
  const location = useLocation();
  const [hasUrlActionError, setUrlActionError] = useState(false);
  const [continueButtonDisabled, setContinueButtonDisabled] = useState(false);

  // Intercept cart URL actions
  useEffect(() => {
    if (!location.search) return;

    try {
      const cartAction = checkForCartAction();

      if (cartAction?.action === 'addBundle') {
        addBundleById(cartAction.bundleId, { needsConfirm: true, needsRedirect: true });
      }
    } catch (e) {
      console.error(e);
      setUrlActionError(true);
      captureException(e);
    } finally {
      const newCartURL = getCartURLWithoutActions();
      if (newCartURL) {
        history.replace(newCartURL);
      }
    }
  }, []);

  useEffect(() => {
    if (!cart) return;

    setContinueButtonDisabled(
      isCartItemQuantityMax(cart.bundle?.hardware) || isCartItemQuantityMax(cart.products)
    );
  }, [cart]);

  const checkForCartAction = (): CartURLActionType | undefined => {
      const cartAction = getCartURLAction();
      if (cartAction === false) return;
      if (!cartAction || !cartAction.action) throw new Error();

      return cartAction;
  };

  const continueButtonClickHandler = () => {
    history.push(redesignV2RoutePath.PURCHASE + CheckoutRoutePath.INFO);
  };

  const renderLoader = () => (
    <div className={styles.cartLoading} role="alert" aria-live="assertive">
      <Image src={loadingGif} alt={t('cart-page.loading')} />
      <TextV2 value={t('cart-page.just-a-sec')} tagName="p" variant="body" />
    </div>
  );

  return (
    <CheckoutProvider>
      <section className={styles.cart}>
        <PageHeaderBanner />
        {cart?.bundle ? (
          <>
            <Grid item xs={12}>
              <header className={styles.progressBar}>
                <ResponsiveProgressBar
                  steps={PROGRESS_BAR_STEPS}
                  currentStep={PROGRESS_BAR_STEP_ENUM.FIRST_STEP}
                />
              </header>
            </Grid>
            <GridContainer gutter justifyContent="center" direction={isMobile ? 'column-reverse' : 'row'} data-test="cart-content">
              {isDesktopLarge && <Grid item xs />}
              <Grid item xs={12} md={8} lg={7} xl={6}>
                <CartProductsList cartData={cart} />
                <MoreAccessories />
              </Grid>
              {(isDesktop || isDesktopLarge) && <Grid item xs />}
              <Grid item xs={12} md={4} xl={3}>
                <OrderSummary
                  continueButtonClickHandler={continueButtonClickHandler}
                  backButtonVisible={false}
                  isCheckout={false}
                  isContinueButtonDisabled={continueButtonDisabled}
                />
              </Grid>
            </GridContainer>
          </>
        ) : isLoading ? (
          renderLoader()
        ) : (
          <EmptyCartPage />
        )}
      </section>
      <StructuredModal
        modalState={[hasUrlActionError, setUrlActionError]}
        title={t('something-went-wrong-title')}
        description={t('something-went-wrong-blurb')}
        actions={[
          {
            label: t('back'),
            variant: LinkVariant.SECONDARY,
            action: () => {
              history.goBack();
            },
          },
        ]}
        data={{
          test: 'url-action-modal',
        }}
      />
    </CheckoutProvider>
  );
};

Cart.displayName = 'Cart';
